import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, LogOut, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AgreementModal = ({ isOpen, user, onAccept, showModal, onClose, agreementType, agreementContent }) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [salesContent, setSalesContent] = useState('');
  const [expanded, setExpanded] = useState(false);

  // Eski modal tipi için (sadece görüntüleme)
  if (showModal !== undefined) {
    if (!Boolean(showModal)) return null;
    
    const formatContent = (content) => {
      if (!content) return '';
      return content
        .split('\n\n')
        .map(para => '<p class="mb-4">' + para.replace(/\n/g, '<br/>') + '</p>')
        .join('');
    };

    return (
      <div 
        className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
        onClick={onClose}
      >
        <div 
          className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/20"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-white/10 bg-white/5">
            <h3 className="text-xl font-bold text-white flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-400" />
              Kullanım Sözleşmesi
            </h3>
            <button onClick={onClose} className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
              ✕
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {agreementContent ? (
              <div className="text-white/90 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: formatContent(agreementContent) }} />
            ) : (
              <p className="text-white/50 text-center py-12">Sözleşme içeriği yükleniyor...</p>
            )}
          </div>
          <div className="p-5 border-t border-white/10 bg-emerald-500/10">
            <div className="flex items-center justify-center gap-2 text-emerald-400">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Bu sözleşmeyi kabul ettiniz</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Yeni kabul modalı
  useEffect(() => {
    if (isOpen) {
      fetchAgreement();
    }
  }, [isOpen]);

  const fetchAgreement = async () => {
    try {
      const response = await axios.get(API + '/settings/agreement/sales', { withCredentials: true });
      setSalesContent(response.data.content || 'Kullanım sözleşmesi içeriği yükleniyor...');
    } catch (error) {
      console.error('Sözleşme yüklenemedi:', error);
      setSalesContent('Sözleşme içeriği yüklenirken hata oluştu.');
    }
  };

  const handleAccept = async () => {
    if (!agreed) {
      alert('Lütfen sözleşmeyi kabul edin.');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(API + '/auth/accept-agreements', {}, { withCredentials: true });
      onAccept();
    } catch (error) {
      console.error('Sözleşme kabul hatası:', error);
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(';').forEach(c => {
      document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });
    window.location.href = '/';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="bg-white/10 backdrop-blur-xl rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/20">
        {/* Header */}
        <div className="p-6 border-b border-white/10 bg-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                <FileText className="w-7 h-7 text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Hoş Geldiniz!</h2>
                <p className="text-blue-200">Devam etmek için sözleşmeyi kabul edin</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl transition-all border border-red-500/30"
            >
              <LogOut className="w-4 h-4" />
              <span>Çıkış Yap</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[55vh]">
          {/* Kullanım Sözleşmesi */}
          <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full p-4 flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-3">
                <div className={"w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all " + 
                  (agreed ? 'bg-emerald-500 border-emerald-500' : 'border-white/30')}>
                  {agreed && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
                <span className="text-white font-medium">Kullanım Sözleşmesi</span>
              </div>
              <ChevronDown className={"w-5 h-5 text-white/50 transition-transform " + (expanded ? 'rotate-180' : '')} />
            </button>
            {expanded && (
              <div className="px-4 pb-4">
                <div className="bg-black/20 rounded-xl p-4 max-h-64 overflow-y-auto text-sm text-white/70 leading-relaxed">
                  {salesContent.split('\n').map((line, i) => (
                    <p key={i} className="mb-2">{line}</p>
                  ))}
                </div>
              </div>
            )}
            <div className="px-4 pb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-5 h-5 rounded border-white/30 bg-white/10 text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-white/80">Kullanım sözleşmesini okudum ve kabul ediyorum</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 bg-white/5">
          <button
            onClick={handleAccept}
            disabled={!agreed || loading}
            className={"w-full py-4 rounded-2xl font-semibold text-lg transition-all flex items-center justify-center gap-2 " +
              (agreed
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                : 'bg-white/10 text-white/40 cursor-not-allowed')}
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>İşleniyor...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Kabul Et ve Devam Et</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgreementModal;
